import * as React from "react";
import { FieldValues, UseControllerProps } from "react-hook-form";

import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import CheckBoxIcon from "@mui/icons-material/CheckBox";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import { createFilterOptions } from "@mui/material/Autocomplete";
import Checkbox from "@mui/material/Checkbox";
import ListItem from "@mui/material/ListItem";
import ListItemText from "@mui/material/ListItemText";
import type { SxProps } from "@mui/material/styles";

import AutocompleteFormField from "components/autocomplete-form-field";
import { useGetAmbientScribeTemplatesQuery } from "services/ambient-scribe";

type SelectScribeTemplateProps = {
  label?: string;
  onOptionSelected?: (
    option:
      | { label: string; value: string }
      | { label: string; value: string }[]
      | null
  ) => void;
  required?: boolean;
  multiple?: boolean;
  sx?: SxProps;
  autoFocus?: boolean;
  openOnFocus?: boolean;
};

const icon = <CheckBoxOutlineBlankIcon fontSize="small" />;
const checkedIcon = <CheckBoxIcon fontSize="small" />;

export type RhfSelectScribeTemplate<T extends FieldValues> =
  SelectScribeTemplateProps & UseControllerProps<T>;

/**
 * Ambient-scribe template picker, for filtering a list by the template that produced its note.
 *
 * `/ai/ambient-scribes/templates/` is not a search endpoint — it returns the practice's whole
 * (small) set, so this loads once and filters client-side. That is the opposite of the
 * server-side-search rule in CLAUDE.md, and deliberately so: there is no `search` round trip to
 * debounce here.
 */
export default function SelectScribeTemplate<T extends FieldValues>({
  control,
  label = "By Template",
  name,
  onOptionSelected,
  required = false,
  multiple = false,
  sx = {},
  autoFocus = true,
  openOnFocus = true
}: RhfSelectScribeTemplate<T>) {
  const filterOptions = createFilterOptions({
    matchFrom: "any",
    stringify: (option: { label: string; value: string }) => option.label
  });

  const { data: templates = [], isLoading } = useGetAmbientScribeTemplatesQuery(
    {}
  );

  const options = React.useMemo(
    () =>
      templates.map((template) => ({
        label: template.name,
        value: String(template.id)
      })),
    [templates]
  );

  return (
    <AutocompleteFormField
      openOnFocus={openOnFocus}
      autoHighlight
      autoFocus={autoFocus}
      name={name}
      label={label}
      isLoading={isLoading}
      required={required}
      multiple={multiple}
      filterOptions={filterOptions}
      options={options}
      getOptionLabel={(option) => option?.label ?? ""}
      renderOption={(props: object, option: any, { selected }: any) => (
        <ListItem {...props}>
          {multiple && (
            <Checkbox
              icon={icon}
              checkedIcon={checkedIcon}
              style={{ marginRight: 8 }}
              checked={selected}
            />
          )}
          <ListItemText>{option?.label}</ListItemText>
        </ListItem>
      )}
      isOptionEqualToValue={(option, value) => option.value === value.value}
      popupIcon={<ArrowDropDownIcon />}
      control={control}
      onOptionSelected={(option) => {
        onOptionSelected?.(option);
      }}
      sx={{ ml: 0.5, mt: 0, minWidth: 220, ...sx }}
    />
  );
}
